import { useEffect, useState } from 'react';
import { GenericAbortSignal } from 'axios';

import { useInfiniteQuery } from '@tanstack/react-query';

import AttrValue, { AttrValueOptions } from 'models/AttrValue';
import { Paginated } from 'models/Paginated';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const attrValuesQueryKeys = {
  index: ['attrValues'] as const,
};

type IndexParams = {
  active?: boolean;
  distinctDisplayName?: boolean;
  type?: string;
  // Leave out the names an admin retired from the pickers. The policy scope filters `active` for
  // everyone but not this, because other surfaces read the same endpoint, so a picker that must not
  // offer a retired name asks for it.
  visible?: boolean;
  // The Attr fed by this workflow value (OLIO-3158). Keyed on that rather than the Attr's name,
  // which is per-client and renameable in admin.
  workflowValueName?: string;
} & BaseIndexQueryParams;

export async function indexAttrValues(params: IndexParams, signal: GenericAbortSignal) {
  return http.get<Paginated<AttrValueOptions>>(`attr_values`, { params, signal }).then((res) => ({
    ...res.data,
    data: res.data.data.map((x) => new AttrValue(x)),
  }));
}

export const useInfiniteAttrValues = (params: IndexParams) => {
  const [search, setSearch] = useState<string>();

  const options = useInfiniteQuery({
    queryKey: [attrValuesQueryKeys.index, { ...params, search }],
    queryFn: ({ signal, pageParam }) => indexAttrValues({ pageSize: 10, ...params, search, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined;
    },
    enabled: false,
  });

  useEffect(() => {
    if (search !== undefined) options.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return { options, onDebouncedSearch: setSearch };
};
